import { useEffect } from "react";
import { Route, Switch } from "wouter";
import { ViewModeProvider } from "./contexts/ViewModeContext";
import { AdminModeProvider } from "./contexts/AdminModeContext";
import { ToastProvider } from "./components/ui/toast";
import { Analytics } from "@vercel/analytics/react";
import { SignedIn, SignedOut, RedirectToSignIn, AuthenticateWithRedirectCallback } from "@clerk/clerk-react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { config } from "./lib/config";
import { LandingPage } from "./pages/LandingPage";
import { HomePage } from "./pages/HomePage";
import { SubmitPage } from "./pages/SubmitPage";
import { LoginPage } from "./pages/LoginPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AdminPage } from "./pages/AdminPage";
import { VenuePage } from "./pages/VenuePage";
import { VenuesPage } from "./pages/VenuesPage";
import { ManageRemindersPage } from "./pages/ManageRemindersPage";
import { ManageNotificationsPage } from "./pages/ManageNotificationsPage";
import { FollowingPage } from "./pages/FollowingPage";
import { StudioDashboardPage } from "./pages/StudioDashboardPage";
import { StudioEventsPage } from "./pages/studio/StudioEventsPage";
import { StudioNoticesPage } from "./pages/studio/StudioNoticesPage";
import { StudioProfilePage } from "./pages/studio/StudioProfilePage";
import { SignUpPage } from "./pages/SignUpPage";
import { AboutPage } from "./pages/AboutPage";

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  return (
    <>
      <SignedIn>
        <Component />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function AuthRoutes() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/events" component={HomePage} />
      <Route path="/submit" component={SubmitPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignUpPage} />
      <Route path="/venue/:id" component={VenuePage} />
      <Route path="/venues" component={VenuesPage} />
      <Route path="/reminders/:token" component={ManageRemindersPage} />
      <Route path="/notifications/:token" component={ManageNotificationsPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/onboarding">
        <ProtectedRoute component={OnboardingPage} />
      </Route>
      <Route path="/dashboard">
        <ProtectedRoute component={DashboardPage} />
      </Route>
      {/* Account-only: following is what an account buys, so this lives behind
          ProtectedRoute and is absent from NoAuthRoutes entirely. */}
      <Route path="/following">
        <ProtectedRoute component={FollowingPage} />
      </Route>
      <Route path="/studio/events">
        <ProtectedRoute component={StudioEventsPage} />
      </Route>
      <Route path="/studio/notices">
        <ProtectedRoute component={StudioNoticesPage} />
      </Route>
      <Route path="/studio/profile">
        <ProtectedRoute component={StudioProfilePage} />
      </Route>
      <Route path="/studio">
        <ProtectedRoute component={StudioDashboardPage} />
      </Route>
      <Route path="/admin">
        <ProtectedRoute component={AdminPage} />
      </Route>
      <Route path="/sso-callback">
        <AuthenticateWithRedirectCallback signInFallbackRedirectUrl="/dashboard" signUpForceRedirectUrl="/onboarding" />
      </Route>
      <Route component={LandingPage} />
    </Switch>
  );
}

function NoAuthRoutes() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/events" component={HomePage} />
      <Route path="/submit" component={SubmitPage} />
      <Route path="/venue/:id" component={VenuePage} />
      <Route path="/venues" component={VenuesPage} />
      <Route path="/reminders/:token" component={ManageRemindersPage} />
      <Route path="/notifications/:token" component={ManageNotificationsPage} />
      <Route path="/about" component={AboutPage} />
      <Route component={LandingPage} />
    </Switch>
  );
}

export function App() {
  // Apply saved theme on mount before first render
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark = saved ? saved === "dark" : prefersDark;
    document.documentElement.classList.toggle("dark", isDark);

    // Organization structured data (Bun strips ld+json from HTML in dev & prod)
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "NE Dances",
      url: "https://nedances.com/",
      logo: "https://nedances.com/og-image.png",
      description: "Find social dance events in New England. Swing, Ballroom, Salsa, Bachata, and more.",
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <APIProvider apiKey={config.googleMapsApiKey}>
      {/* Vercel Web Analytics. The /react entry, not /next — this is a Bun SPA
          on wouter, and @vercel/analytics/next imports next/navigation, which
          fails to resolve here. Route changes are tracked via the History API,
          which wouter uses. */}
      <Analytics />
      <ToastProvider>
        <ViewModeProvider>
          <AdminModeProvider>
            {config.clerkPublishableKey ? <AuthRoutes /> : <NoAuthRoutes />}
          </AdminModeProvider>
        </ViewModeProvider>
      </ToastProvider>
    </APIProvider>
  );
}
